import React, { useState, useEffect, useContext, useRef } from 'react';
import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  CircularProgress,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  InputAdornment,
  FormHelperText,
  Grid,
} from '@material-ui/core';
import CheckCircleOutlineIcon from '@material-ui/icons/CheckCircleOutline';
import PrintIcon from '@material-ui/icons/Print';
import Alert from '@material-ui/lab/Alert';
import axios from 'axios';
import StoreContext from '../../Store/context';
import { API_BASE_URL } from '../../../utils/useApi';
import { usePOS } from '../../../contexts/POSContext';
import { formattedCurrency, emptyObject } from '../../../utils/utils';
import PrintPreviewModal from '../../Sale/Print/PrintPreviewModal';
import UIDialogTitle from '../../UI/Dialog/Title/UIDialogTitle';
import CurrencyTextField from '@unicef/material-ui-currency-textfield/dist/CurrencyTextField';

const POSCheckoutModal = ({ open, onClose }) => {
  const { credencials, setModalCashierSelect } = useContext(StoreContext);
  const {
    cartItems,
    totals,
    selectedCustomer,
    appliedPromotion,
    appliedCoupon,
    observation,
    paymentMethods,
    clearCart,
    syncCatalog,
    isDarkMode,
  } = usePOS();

  const [selectedMethodId, setSelectedMethodId] = useState('');
  const [currentPlots, setCurrentPlots] = useState(1);
  const [customInterestRate, setCustomInterestRate] = useState(0);
  const [amountReceived, setAmountReceived] = useState('');
  const [paymentList, setPaymentList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState(null);
  const [successSaleData, setSuccessSaleData] = useState(null);
  const [openPrintModal, setOpenPrintModal] = useState(false);
  const amountInputRef = useRef(null);

  const selectedMethodObj = paymentMethods.find((m) => String(m.id) === String(selectedMethodId));

  // Restrição de métodos por promoção
  const promoObj = appliedPromotion || appliedCoupon;
  const promoPaymentRestrictions = (() => {
    if (!promoObj || promoObj.id === 1 || !promoObj.paymentMethods) return [];
    if (Array.isArray(promoObj.paymentMethods)) return promoObj.paymentMethods.map(String);
    try {
      const parsed = JSON.parse(promoObj.paymentMethods);
      return Array.isArray(parsed) ? parsed.map(String) : [];
    } catch (e) {
      return [];
    }
  })();

  const availableMethods = promoPaymentRestrictions.length > 0
    ? paymentMethods.filter((m) => promoPaymentRestrictions.includes(String(m.id)))
    : paymentMethods;

  const prevOpenRef = useRef(false);
  const isSubmittingRef = useRef(false);

  const [serverTotalOverride, setServerTotalOverride] = useState(null);

  // Ao abrir o modal, inicializa valores apenas na transição de abertura
  useEffect(() => {
    if (open && !prevOpenRef.current) {
      isSubmittingRef.current = false;
      setErrorMsg(null);
      setSuccessSaleData(null);
      setOpenPrintModal(false);
      setServerTotalOverride(null);
      const defaultMethod = availableMethods.length > 0 ? availableMethods[0].id : '';
      setSelectedMethodId(defaultMethod);
      setCurrentPlots(1);
      setCustomInterestRate(0);
      setPaymentList([]);
      setTimeout(() => {
        if (amountInputRef.current) amountInputRef.current.focus();
      }, 100);
    }
    prevOpenRef.current = open;
  }, [open]);

  // Atualiza juros quando o método ou parcela mudam
  useEffect(() => {
    if (!selectedMethodObj) return;

    let autoInterest = 0;
    if (selectedMethodObj.interest && currentPlots > 1) {
      let interestConfig = selectedMethodObj.interest;
      if (typeof interestConfig === 'string') {
        try {
          interestConfig = JSON.parse(interestConfig);
        } catch (e) {
          interestConfig = {};
        }
      }
      const foundRate = interestConfig[currentPlots] || interestConfig[String(currentPlots)];
      if (foundRate !== undefined && foundRate !== null) {
        autoInterest = parseFloat(foundRate) || 0;
      }
    }
    setCustomInterestRate(autoInterest);
  }, [selectedMethodId, currentPlots, selectedMethodObj]);

  // Cálculos de juros e parcelamento em tempo real
  const baseValue = Math.max(0, totals.subtotal - totals.totalDiscount);
  const activeInterestRate = parseFloat(customInterestRate) || 0;
  const interestIncrease = activeInterestRate > 0 ? (baseValue * activeInterestRate) / 100 : 0;
  const computedTotal = baseValue + interestIncrease;
  const finalTotalToPay = serverTotalOverride !== null ? serverTotalOverride : computedTotal;
  const safePlots = Math.max(1, parseInt(currentPlots, 10) || 1);
  const valuePerPlot = finalTotalToPay / safePlots;

  // Sincroniza o valor recebido sugerido
  useEffect(() => {
    if (open && !successSaleData) {
      setAmountReceived(finalTotalToPay.toFixed(2));
    }
  }, [finalTotalToPay, open, successSaleData]);

  // Cálculo de troco e restante
  const receivedNum = parseFloat(amountReceived) || 0;
  const changeAmount = Math.max(0, receivedNum - finalTotalToPay);
  const remainingAmount = Math.max(0, finalTotalToPay - receivedNum);

  // Lista padrão de opções rápidas de parcelamento
  const plotsOptions = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 18, 24];

  // Navegação nas formas de pagamento pelas setas do teclado
  const navigatePaymentMethod = (direction) => {
    if (availableMethods.length <= 1) return;
    const currentIdx = availableMethods.findIndex(
      (m) => String(m.id) === String(selectedMethodId)
    );
    const validIdx = currentIdx >= 0 ? currentIdx : 0;
    let nextIdx = validIdx + direction;
    if (nextIdx < 0) nextIdx = availableMethods.length - 1;
    if (nextIdx >= availableMethods.length) nextIdx = 0;
    setSelectedMethodId(availableMethods[nextIdx].id);
  };

  const handleFinalizeSale = async () => {
    if (isSubmittingRef.current || loading || openPrintModal) return;
    isSubmittingRef.current = true;
    setLoading(true);
    setErrorMsg(null);

    if (!credencials?.cashSession) {
      setErrorMsg('Caixa fechado! Selecione ou abra um caixa antes de vender.');
      if (setModalCashierSelect) setModalCashierSelect(true);
      isSubmittingRef.current = false;
      setLoading(false);
      return;
    }

    if (cartItems.length === 0) {
      setErrorMsg('Carrinho vazio.');
      isSubmittingRef.current = false;
      setLoading(false);
      return;
    }

    const productsDict = {};
    const servicesDict = {};

    cartItems.forEach((item) => {
      if (item.typeItem === 'product') {
        productsDict[item.id] = (productsDict[item.id] || 0) + parseFloat(item.amount);
      } else if (item.typeItem === 'service') {
        servicesDict[item.id] = (servicesDict[item.id] || 0) + parseFloat(item.amount);
      }
    });

    const effectivePaid = Math.min(finalTotalToPay, Math.max(0, receivedNum));

    const finalPayments = (paymentList.length > 0
      ? paymentList
      : [
          {
            paymentMethod: Number(selectedMethodId || (availableMethods[0] && availableMethods[0].id) || 1),
            amountPaid: Number(effectivePaid.toFixed(2)),
          },
        ]
    ).map((p) => ({
      paymentMethod: Number(p.paymentMethod || p.method || 1),
      amountPaid: Number(parseFloat(p.amountPaid || 0).toFixed(2)),
    }));

    const payload = {
      onlyCalculate: false,
      userId: Number(credencials.id),
      cashRegister: Number(credencials.cashSession),
      customer: selectedCustomer ? Number(selectedCustomer.id) : 1,
      promotion: promoObj ? Number(promoObj.id) : 1,
      plots: safePlots,
      interest: activeInterestRate > 0 ? { percentage: activeInterestRate, type: 'simples' } : {},
      splitRemaining: false,
      setDiscount: Number(totals.manualDiscount) || 0,
      observation: observation || '',
      products: productsDict,
      services: servicesDict,
      paymentData: finalPayments,
    };

    try {
      // 1. Pré-validação com onlyCalculate: true para checar consistência de preços e estoque
      const checkPayload = {
        ...payload,
        onlyCalculate: true,
      };

      const checkResponse = await axios.post(`${API_BASE_URL}/sales`, checkPayload, {
        headers: {
          sessionToken: credencials.sessionToken,
          company: String(credencials.company),
        },
      });

      const serverCalculatedTotal = parseFloat(checkResponse.data?.total || 0);

      // 2. Se houver divergência entre o total esperado e o total calculado pelo servidor
      const diff = Math.abs(serverCalculatedTotal - finalTotalToPay);
      if (diff > 0.02) {
        setServerTotalOverride(serverCalculatedTotal);
        setAmountReceived(serverCalculatedTotal);
        await syncCatalog(true);
        setErrorMsg(
          `Atenção: O valor de um ou mais itens foi atualizado no servidor. ` +
          `Total anterior: ${formattedCurrency(finalTotalToPay)} → Novo total oficial: ${formattedCurrency(serverCalculatedTotal)}. ` +
          `Por favor, confira o novo valor com o cliente e confirme a venda novamente.`
        );
        isSubmittingRef.current = false;
        setLoading(false);
        return;
      }

      // 3. Estando tudo correto, grava a venda definitivamente
      const finalizeResponse = await axios.post(`${API_BASE_URL}/sales`, payload, {
        headers: {
          sessionToken: credencials.sessionToken,
          company: String(credencials.company),
        },
      });

      const responseData = finalizeResponse.data;
      const finishedSale = {
        ...responseData,
        value: responseData.value !== undefined ? responseData.value : finalTotalToPay,
        total: finalTotalToPay,
        customer: selectedCustomer,
        items: cartItems,
        userName: `${credencials?.name || ''} ${credencials?.lastName || ''}`.trim() || credencials?.name,
      };

      setSuccessSaleData(finishedSale);
      setOpenPrintModal(true);
      clearCart();
      syncCatalog(true);
    } catch (err) {
      console.error('Erro ao finalizar venda:', err);
      const serverMsg = err.response?.data?.message || err.response?.data?.error || 'Erro ao processar venda no servidor.';
      setErrorMsg(serverMsg);
      syncCatalog(false);
      isSubmittingRef.current = false;
    } finally {
      setLoading(false);
    }
  };

  const handleFinalizeSaleRef = useRef(handleFinalizeSale);
  handleFinalizeSaleRef.current = handleFinalizeSale;

  const handleClosePrintModal = () => {
    isSubmittingRef.current = false;
    setOpenPrintModal(false);
    setSuccessSaleData(null);
    onClose();
  };

  useEffect(() => {
    if (!open || openPrintModal) return;

    const handleModalKeyDown = (e) => {
      const isEnter = e.key === 'Enter' || e.code === 'Enter' || e.code === 'NumpadEnter' || e.keyCode === 13;
      if (isEnter) {
        const isBackBtn = document.activeElement && document.activeElement.id === 'pos-checkout-btn-back';
        if (isBackBtn) return;

        e.preventDefault();
        e.stopPropagation();
        if (!isSubmittingRef.current && !loading && !openPrintModal && handleFinalizeSaleRef.current) {
          handleFinalizeSaleRef.current();
        }
      } else if (e.key === 'ArrowUp' || e.key === 'ArrowLeft') {
        e.preventDefault();
        navigatePaymentMethod(-1);
      } else if (e.key === 'ArrowDown' || e.key === 'ArrowRight') {
        e.preventDefault();
        navigatePaymentMethod(1);
      }
    };

    window.addEventListener('keydown', handleModalKeyDown, true);
    return () => window.removeEventListener('keydown', handleModalKeyDown, true);
  }, [open, openPrintModal, loading]);

  return (
    <>
      <Dialog
        open={open && !openPrintModal}
        onClose={loading ? undefined : onClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          component: 'form',
          onSubmit: (e) => {
            e.preventDefault();
            if (!isSubmittingRef.current && !loading && !openPrintModal) {
              handleFinalizeSale();
            }
          },
          className: isDarkMode ? 'pos-dark-paper' : '',
        }}
      >
        <UIDialogTitle onClose={loading ? undefined : onClose}>
          Finalização da Venda [Enter]
        </UIDialogTitle>

        <DialogContent dividers style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {errorMsg && (
            <Alert severity="error">
              {errorMsg}
            </Alert>
          )}

          {/* Total da Venda e Informações de Parcelamento */}
          <div
            style={{
              background: '#f5f5f5',
              border: '1px solid #e0e0e0',
              borderRadius: 4,
              padding: 16,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <div>
              <div style={{ fontSize: '0.8rem', color: '#616161' }}>Valor Total a Pagar</div>
              <div style={{ fontSize: '1.8rem', fontWeight: 900, color: '#185421' }}>
                {formattedCurrency(finalTotalToPay)}
              </div>
              <div style={{ fontSize: '0.9rem', color: '#17497F', fontWeight: 700, marginTop: 2 }}>
                {safePlots > 1 ? `Pagamento em ${safePlots}x de ${formattedCurrency(valuePerPlot)}` : 'Pagamento à Vista (1x)'}
                {activeInterestRate > 0 ? ` (Juros de ${activeInterestRate}%)` : ''}
              </div>
            </div>
            {selectedCustomer && (
              <div style={{ textAlign: 'right' }}>
                <div style={{ fontSize: '0.8rem', color: '#616161' }}>Cliente</div>
                <div style={{ fontWeight: 700, color: '#17497F' }}>
                  {`${selectedCustomer.name} ${selectedCustomer.lastName || selectedCustomer.last_name || ''}`.trim()}
                </div>
              </div>
            )}
          </div>

          {/* Forma de Pagamento */}
          <FormControl variant="outlined" fullWidth size="small">
            <InputLabel>Forma de Pagamento</InputLabel>
            <Select
              value={selectedMethodId}
              onChange={(e) => setSelectedMethodId(e.target.value)}
              label="Forma de Pagamento"
              MenuProps={{
                PaperProps: {
                  className: isDarkMode ? 'pos-dark-paper' : '',
                },
              }}
            >
              {availableMethods.map((m) => (
                <MenuItem key={m.id} value={m.id}>
                  {m.name}
                </MenuItem>
              ))}
            </Select>
            <FormHelperText style={{ color: '#757575', fontSize: '0.72rem' }}>
              Use as setas ⬅ ➡ ou ⬆ ⬇ no teclado para alternar a forma de pagamento
            </FormHelperText>
          </FormControl>

          {/* Opções de Parcelamento e Juros (Condicionais ou Universais) */}
          <div style={{ display: 'flex', gap: 12 }}>
            <FormControl variant="outlined" size="small" style={{ flex: 1.2 }}>
              <InputLabel>Parcelamento</InputLabel>
              <Select
                value={currentPlots}
                onChange={(e) => setCurrentPlots(Number(e.target.value))}
                label="Parcelamento"
                MenuProps={{
                  PaperProps: {
                    className: isDarkMode ? 'pos-dark-paper' : '',
                  },
                }}
              >
                {plotsOptions.map((p) => (
                  <MenuItem key={p} value={p}>
                    {p}x {p === 1 ? 'à vista' : `de ${formattedCurrency(finalTotalToPay / p)}`}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              label="Aplicar Juros (%)"
              type="number"
              variant="outlined"
              size="small"
              style={{ flex: 0.8 }}
              value={customInterestRate}
              onChange={(e) => setCustomInterestRate(e.target.value)}
              InputProps={{
                endAdornment: <InputAdornment position="end">%</InputAdornment>,
              }}
              helperText={activeInterestRate > 0 ? `+ ${formattedCurrency(interestIncrease)}` : 'Isento'}
            />
          </div>

          {/* Valor Recebido e Troco */}
          <div style={{ display: 'flex', gap: 12 }}>
            <CurrencyTextField
              label="Valor Recebido (R$)"
              variant="outlined"
              size="small"
              fullWidth
              currencySymbol="R$"
              decimalCharacter=","
              digitGroupSeparator="."
              outputFormat="number"
              value={amountReceived}
              onChange={(event, value) => {
                setAmountReceived(value !== undefined && value !== null ? value : 0);
              }}
            />

            <div
              style={{
                flex: 1,
                background: '#fafafa',
                border: '1px solid #e0e0e0',
                borderRadius: 4,
                padding: '6px 14px',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
              }}
            >
              <div style={{ fontSize: '0.75rem', color: '#757575' }}>
                {receivedNum < finalTotalToPay
                  ? (receivedNum === 0 ? 'A Prazo (Fiado)' : 'Restante a Pagar')
                  : 'Troco'}
              </div>
              <div
                style={{
                  fontSize: '1.2rem',
                  fontWeight: 800,
                  color:
                    receivedNum < finalTotalToPay
                      ? '#d97706'
                      : changeAmount > 0
                        ? '#17497F'
                        : '#9e9e9e',
                }}
              >
                {formattedCurrency(
                  receivedNum < finalTotalToPay ? remainingAmount : changeAmount
                )}
              </div>
            </div>
          </div>

          {/* Alerta caso seja venda fiado/a prazo sem cliente selecionado */}
          {remainingAmount > 0 && !selectedCustomer && (
            <Alert severity="warning" style={{ fontSize: '0.82rem' }}>
              Atenção: Venda a prazo/fiado sem cliente selecionado. O débito será vinculado ao cliente avulso.
            </Alert>
          )}
        </DialogContent>

        <DialogActions style={{ padding: '8px 16px', justifyContent: 'space-between' }}>
          <Button
            id="pos-checkout-btn-back"
            onClick={onClose}
            disabled={loading}
            color="default"
          >
            Voltar [Esc]
          </Button>
          <Button
            id="pos-checkout-btn-confirm"
            type="submit"
            variant="contained"
            disabled={loading}
            style={{
              backgroundColor: '#185421',
              color: '#ffffff',
              fontWeight: 700,
              fontSize: '1rem',
              padding: '8px 24px',
              textTransform: 'none',
              pointerEvents: (loading || isSubmittingRef.current) ? 'none' : 'auto',
            }}
          >
            {loading ? <CircularProgress size={24} color="inherit" /> : 'Confirmar Venda [Enter]'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal de Impressão de Comprovante Direto */}
      {openPrintModal && successSaleData && (
        <PrintPreviewModal
          open={openPrintModal}
          handleClose={handleClosePrintModal}
          saleData={successSaleData}
          credencials={credencials}
          options={{ customer: selectedCustomer, plots: safePlots }}
        />
      )}
    </>
  );
};

export default POSCheckoutModal;
